import { fetchFrequentlyBuyTogetherProduct } from 'components/FrequentlyBuyTogether/actions';
import { createMatchSelector, push } from 'connected-react-router';
import { setAlcoholState } from 'containers/MainLayout/actions';
import { call, fork, put, select, take } from 'redux-saga/effects';
import translations from 'translations';
import { Product } from 'types/schema';
import {
  countFeedbackRatingApi,
  feedbackProductApi,
  getProduct,
  getProductBySupplierInternalCode,
  getProductFeedbacksApi,
  getProducts,
  getUnreviewedOrderItemsApi,
  getVouchersForProductApi,
} from 'utils/apollo';
import { LANGUAGES } from 'utils/constants';
import generateProductSlug from 'utils/generateProductSlug';
import isValidUUID from 'utils/isValidUUID';
import parseSlug from 'utils/parseSlug';
import { getItemsInSameProductFamilyApi, getItemsInSameProductGroupsApi } from '../../utils/apollo';
import MainLayoutActionTypes from '../MainLayout/constants';
import { selectBuyer, selectStore } from '../MainLayout/selectors';
import {
  product as productActions,
  productItemsInProductFamily,
  productItemsInProductGroups,
  productReviews as productReviewsActions,
  relateProducts as relateProductsActions,
  setVouchersForProduct,
} from './actions';

import { message } from 'antd';
import ProductDetailActionTypes from './constants';
import messages from './messages';
import { selectProduct } from './selectors';

function generateProductUrl(product: Product, lang: string) {
  return `${generateProductSlug(product.nameLocal?.[lang], product.supplierInternalCode)}${lang === 'vi' ? '' : `?lang=${lang}`}`;
}

function* getRelateProducts(product) {
  yield put(relateProductsActions.request());

  const response = yield call(getProducts, {
    sort: [],
    filter: {
      categoryName: `${product.category.parent.name}/${product.category.name}`,
    },
    pagination: {
      page: 0,
      size: 25,
    },
    isBoostKamereoProducts: true,
  });
  if (!response.errors) {
    const data = response.data.filter((item) => item.id !== product.id).slice(0, 24);
    yield put(relateProductsActions.success(data));
  } else {
    yield put(relateProductsActions.failure(response.errors));
  }
}

function* getItemsInSameProductGroups(product) {
  yield put(productItemsInProductGroups.request());

  const loggedIn = !!localStorage.getItem('token');
  const regionLocal = localStorage.getItem('region');
  let buyer = yield select(selectBuyer());
  if (!buyer || !Object?.keys(buyer)?.length) {
    yield take(MainLayoutActionTypes.FETCH_BUYER_SUCCESS);
    buyer = yield select(selectBuyer());
  }
  const response = yield call(getItemsInSameProductGroupsApi, {
    productIds: [product.id],
    pagination: {
      page: 0,
      size: 1000,
    },
    regionCode: loggedIn ? buyer?.region : regionLocal || 'HCM',
  });
  if (!response.errors) {
    yield put(productItemsInProductGroups.success(response.data));
  } else {
    yield put(productItemsInProductGroups.failure(response.errors));
  }
}

function* getItemsInSameProductFamily(product) {
  yield put(productItemsInProductFamily.request());
  const response = yield call(getItemsInSameProductFamilyApi, {
    itemUuid: product.id,
  });
  if (!response.errors) {
    yield put(productItemsInProductFamily.success(response.data));
  } else {
    yield put(productItemsInProductFamily.failure(response.errors));
  }
}

function* getVouchersForProduct(product) {
  const response = yield call(getVouchersForProductApi, {
    itemUuid: product.id,
  });
  if (!response.errors) {
    yield put(setVouchersForProduct(response.data));
  }
}

function* countFeedbackRating(productId?: string) {
  const product = yield select(selectProduct());
  let reviewsProductId = productId;
  if (!reviewsProductId && product.id) {
    productId = product.id;
  }
  if (!reviewsProductId) {
    return;
  }

  const response = yield call(countFeedbackRatingApi, { productId: reviewsProductId });
  if (!response.errors) {
    yield put(
      productReviewsActions.success({
        countFeedbackRating: response.data,
      }),
    );
  }
}

function* getProductReviewData({
  productId,
  page = 0,
  rating,
}: {
  productId?: string;
  page?: number;
  rating?: number;
}) {
  const product = yield select(selectProduct());

  let reviewsProductId = productId;
  if (!reviewsProductId && product.id) {
    reviewsProductId = product.id;
  }
  if (!reviewsProductId) {
    return;
  }

  const response = yield call(getProductFeedbacksApi, {
    productId: reviewsProductId,
    pagination: {
      page,
      size: 5,
    },
    rating: rating ? [rating] : undefined,
  });

  if (!response.errors) {
    yield put(
      productReviewsActions.success({
        ...response,
      }),
    );
  } else {
    yield put(productReviewsActions.failure(response.errors));
  }
}

function* filterProductReviewsFlow() {
  while (true) {
    const { payload } = yield take(ProductDetailActionTypes.FETCH_PRODUCT_REVIEWS_REQUEST);
    yield call(getProductReviewData, payload);
  }
}

function* checkCanReviewProduct() {
  while (true) {
    const { payload } = yield take(ProductDetailActionTypes.CHECK_CAN_REVIEW_PRODUCT);
    const product = yield select(selectProduct());
    const buyer = yield select(selectBuyer());

    if (!product?.id || !buyer?.id) {
      payload.callback(false);
      continue;
    }

    try {
      const response = yield call(getUnreviewedOrderItemsApi, {
        input: {
          productId: product.id,
          buyerId: Number(buyer.id),
        },
      });
      if (!response?.errors) {
        if (!response?.success) {
          message.error(response?.userErrors[0]?.message || translations(messages.somethingWentWrong));
          payload.callback(false);
        } else {
          payload.callback(true);
        }
      } else {
        message.error(response?.errors[0]?.message || translations(messages.somethingWentWrong));
        payload.callback(false);
      }
    } catch (error) {
      message.error(translations(messages.somethingWentWrong));
      payload.callback(false);
    }
  }
}

function* submitReviewProduct() {
  while (true) {
    const { payload } = yield take(ProductDetailActionTypes.SUBMIT_REVIEW_PRODUCT);
    const store = yield select(selectStore());

    if (!store?.id) {
      payload.callback(false);
      continue;
    }

    try {
      const response = yield call(feedbackProductApi, {
        input: {
          ...payload.input,
          storeId: store?.id,
        },
      });
      if (!response?.errors) {
        if (response?.success) {
          message.success(translations(messages.submitReviewSuccessfully));
          payload.callback(true);
          yield put(
            productReviewsActions.request({
              productId: payload.input.objectId,
              page: 0,
              rating: undefined,
            }),
          );
          yield call(countFeedbackRating, payload.input.objectId);
        } else {
          message.error(response?.userErrors[0]?.message || translations(messages.somethingWentWrong));
          payload.callback(false);
        }
      } else {
        message.error(response?.errors[0]?.message || translations(messages.somethingWentWrong));
        payload.callback(false);
      }
    } catch (error) {
      message.error(translations(messages.somethingWentWrong));
      payload.callback(false);
    }
  }
}

function* handleProductDetailsSuccess(response: Product, localLang: string, alternateLang: string) {
  yield put(
    productActions.success({
      ...response,
      loading: false,
      localLang: localLang,
      alternateLang: alternateLang,
      validProductUrl: generateProductUrl(response, localLang),
      alternateProductUrl: generateProductUrl(response, alternateLang),
    }),
  );
  window.localStorage.setItem('lang', localLang as string);
  import('utils/triggerGA4Event')
    .then((module) => {
      const triggerGA4EcommerceEvent = module.default;
      const { EventName } = module;
      triggerGA4EcommerceEvent(EventName.VIEW_ITEM, response);
    })
    .catch((error) => {
      // tslint:disable-next-line: no-console
      console.error('Failed to load GA4 module:', error);
    });

  yield fork(getItemsInSameProductGroups, response);
  yield fork(getItemsInSameProductFamily, response);
  yield fork(getProductReviewData, { productId: response.id, page: 0 });
  yield fork(countFeedbackRating, response.id);
  yield fork(getVouchersForProduct, response);
  yield fork(getRelateProducts, response);
}

function* getProductDetails() {
  try {
    const route = yield select(createMatchSelector('/products/:slug'));
    const slug = route.params.slug;
    const localLang = localStorage.getItem('lang') || LANGUAGES.Vi;
    const alternateLang = localLang === LANGUAGES.Vi ? LANGUAGES.Alternate : LANGUAGES.Vi;
    let uuid: string = isValidUUID(slug) && slug;
    // const isNotPreloadState = loggedIn || !state.product?.id || state.product?.supplierInternalCode !== parseSlug(slug);
    yield put(productActions.request());
    if (!uuid) {
      const supplierInternalCode = parseSlug(route.params.slug);

      const productBySkuCodeResponse = yield call(getProductBySupplierInternalCode, {
        supplierInternalCode: supplierInternalCode,
      });
      uuid = productBySkuCodeResponse.id;
      yield put(fetchFrequentlyBuyTogetherProduct({ supplierInternalCode: supplierInternalCode }));
    }
    // To fetch full information of product
    const response = yield call(getProduct, { id: uuid });
    if (!response.errors) {
      yield handleProductDetailsSuccess(response, localLang, alternateLang);
    } else {
      yield put(push('/not-found'));
    }
    const isUnder18FromLocalStorage = window.localStorage.getItem('isUnder18')
      ? window.localStorage.getItem('isUnder18') === 'true'
      : true;
    const isAlcoholCategory = response?.category?.parent?.name === 'Alcohol';
    if (isUnder18FromLocalStorage && isAlcoholCategory) {
      yield put(setAlcoholState(true));
    }
  } catch (error) {
    yield put(productActions.failure(error as Error));
  }
}

function* initData() {
  yield call(getProductDetails);
}

// Individual exports for testing
export default function* productDetailsPageSaga() {
  // See example in containers/HomePage/saga.js
  yield fork(initData);
  yield fork(filterProductReviewsFlow);
  yield fork(checkCanReviewProduct);
  yield fork(submitReviewProduct);
}
